import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from '../types';
import { Link } from 'react-router-dom';
import { getApiUrl } from '../config';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { isSuperAdmin, getEffectiveRole, getOrgRoleDisplayName } from '../utils/roleUtils';

interface SettingsProps {
  user: User;
  i18n: any;
  isLinkingCalendar: boolean;
  handleLinkGoogleCalendar: () => void;
  handleUnlinkGoogleCalendar: () => void;
  refreshUserData?: (userId: string) => void;
}

export default function Settings({
  user,
  i18n,
  isLinkingCalendar,
  handleLinkGoogleCalendar,
  handleUnlinkGoogleCalendar,
  refreshUserData
}: SettingsProps) {
  const { t } = useTranslation();
  const langPrefix = i18n?.language ? `/${i18n.language}` : '';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [profileImageError, setProfileImageError] = useState(false);
  const [organizationName, setOrganizationName] = useState<string>('');
  const [loadingOrg, setLoadingOrg] = useState(false);

  const { systemRole, organizationRole, organizationId } = getEffectiveRole(user);

  // Load organization name if user belongs to one
  useEffect(() => {
    const loadOrganization = async () => {
      if (organizationId) {
        setLoadingOrg(true);
        try {
          const orgDoc = await getDoc(doc(db, 'organizations', organizationId));
          if (orgDoc.exists()) {
            setOrganizationName(orgDoc.data().name || 'Unknown Organization');
          }
        } catch (error) {
          console.error('Error loading organization:', error);
        } finally {
          setLoadingOrg(false);
        }
      }
    };

    loadOrganization();
  }, [organizationId]);

  const handleProfilePictureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      console.error('Invalid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      console.error('File too large');
      return;
    }

    setIsUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(getApiUrl('/upload'), {
        method: 'POST',
        body: formDataUpload,
      });

      if (response.ok) {
        const result = await response.json();
        const imageUrl = result.file_url;

        if (imageUrl && user.id) {
        // Update user profile picture in Firebase
          try {
        const userRef = doc(db, 'users', user.id);
        await updateDoc(userRef, {
          picture: imageUrl
        });

            // Refresh user data to show new image
        if (refreshUserData) {
          await refreshUserData(user.id);
            }
            
            // Profile picture updated successfully
          } catch (firebaseError) {
            console.error('Firebase update failed:', firebaseError);
            console.error('Failed to update profile');
          }
        } else {
          console.error('Missing image URL or user ID');
        }

        setUploadError(null);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      console.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };
  
  return (
    <div className="settings-page">
      <div className="settings-header">
        <h2>{t('settings.title')}</h2>
      </div>
      
      <div className="settings-content">
        <div className="settings-section">
          <h3>{t('settings.accountInfo')}</h3>
          <div className="account-info">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: user.picture ? 'transparent' : '#6c757d',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    border: '2px solid #ddd'
                  }}
                >
              {user.picture && !profileImageError ? (
                <img
                  src={user.picture}
                  alt="Profile"
                  onError={(e) => {
                    setProfileImageError(true);
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              ) : null}
              {(!user.picture || profileImageError) && (
                <span style={{ fontSize: '32px', color: 'white' }}>
                  👤
                </span>
              )}
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureUpload}
                  style={{ display: 'none' }}
                />
                
                <button
                  onClick={() => {
                    console.log('Change Photo button clicked');
                    fileInputRef.current?.click();
                  }}
                  disabled={isUploading}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    cursor: isUploading ? 'not-allowed' : 'pointer',
                    opacity: isUploading ? 0.6 : 1
                  }}
                >
                  {isUploading ? 'Uploading...' : 'Change Photo'}
                </button>
                
                {uploadError && (
                  <div style={{ color: '#dc3545', fontSize: '12px', textAlign: 'center' }}>
                    {uploadError}
                  </div>
                )}
              </div>
              
              <div style={{ flex: 1 }}>
                <p><strong>{t('settings.name')}:</strong> {user.name}</p>
                <p><strong>{t('settings.email')}:</strong> {user.email}</p>
                
                {user.organizationId && (
                  <p>
                    <strong>{t('settings.organization')}:</strong>{' '}
                    {loadingOrg ? t('settings.loading') : (organizationName || t('settings.noOrganization'))}
                  </p>
                )}
                
                {!isSuperAdmin(user) && organizationRole && (
                  <p>
                    <strong>{t('settings.role')}:</strong>{' '}
                    <span className={`role-badge ${organizationRole}`}>
                      {getOrgRoleDisplayName(organizationRole, t)}
                    </span>
                  </p>
                )}
                {isSuperAdmin(user) && (
                  <p>
                    <strong>{t('settings.role')}:</strong>{' '}
                    <span className={`role-badge super_admin`}>
                      {t('users.roles.super_admin')}
                    </span>
                  </p>
                )}
                
                <p><strong>{t('settings.created')}:</strong> {new Date(user.createdAt || '').toLocaleDateString('en-GB')}</p>
                <p><strong>{t('settings.lastLogin')}:</strong> {new Date(user.lastLoginAt || '').toLocaleDateString('en-GB')}</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="settings-section">
          <h3>{t('settings.googleIntegration')}</h3>
          <div className="google-integration-grid">
            <div className="integration-item">
              <div className="integration-header">
                <span className="integration-icon">📅</span>
                <span className="integration-title">{t('settings.googleCalendar')}</span>
                <span className={`integration-status ${user.hasGoogleCalendar ? 'linked' : 'not-linked'}`}>
                  {user.hasGoogleCalendar ? t('settings.linked') : t('settings.notLinked')}
                </span>
              </div>
              {user.hasGoogleCalendar ? (
                <div className="integration-details">
                  <p className="integration-description">
                    {t('settings.googleCalendarLinked')}
                  </p>
                  <button 
                    onClick={handleUnlinkGoogleCalendar}
                    disabled={isLinkingCalendar}
                    className="unlink-google-btn"
                  >
                    {isLinkingCalendar ? (
                      <>
                        <span className="spinner"></span>
                        {t('settings.unlinking')}
                      </>
                    ) : (
                      <>
                        🔓 {t('settings.unlinkGoogleCalendar')}
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="integration-details">
                  <p className="integration-description">
                    {t('settings.googleCalendarNotLinked')}
                  </p>
                  <button 
                    onClick={handleLinkGoogleCalendar}
                    disabled={isLinkingCalendar}
                    className="link-google-btn"
                  >
                    {isLinkingCalendar ? (
                      <>
                        <span className="spinner"></span>
                        {t('settings.linking')}
                      </>
                    ) : (
                      <>
                        📅 {t('settings.linkGoogleCalendar')}
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="settings-section">
          <h3>{t('settings.legal')}</h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Link
              to={`${langPrefix}/privacy`}
              style={{
                padding: '10px 16px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                textDecoration: 'none',
                color: '#007acc',
                background: '#fff'
              }}
            >
              {t('settings.privacyPolicy')}
            </Link>
            <Link
              to={`${langPrefix}/terms`}
              style={{
                padding: '10px 16px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                textDecoration: 'none',
                color: '#007acc',
                background: '#fff'
              }}
            >
              {t('settings.termsOfService')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
